import React, { useEffect, useState } from 'react'
import { Button, Form, Modal, Space } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'antd/lib/form/Form'
import { contextCompanyIdSelector } from '../../../../../redux/context/company/selectors'
import { currentUserSortedCompaniesSelector } from '../../../../../redux/session/currentUser/selectors'
import { CustomReport } from '../../../../../redux/context/customReports/types'
import { customReportDateSelector } from '../../../../../redux/context/customReports/selectors'
import CustomReportSectionCopyForm from './CustomReportSectionCopyForm'
import {
  CustomReportSectionsType,
  CustomReportTableSection,
  CustomReportChartSection,
  CustomReportPieSection,
  CustomReportInfoboxSection
} from '../../../../../redux/context/customReports/typesSection'
import { CustomReportVariable, RowType } from '../../../../../redux/context/customReports/typesVariable'
import { parseInfoboxSection } from '../infobox/infoboxCategoryParser'
import CustomReportSectionForm from './CustomReportSectionForm'
import { showErrorMessage, showSuccessMessage } from '../../../../../utils/message'
import { useDeleteConfirmModal } from '../../../../../shared/modals'
import { getTitleByLanguage } from '../customReportTable/utils'
import customReportQueryKeys, {
  isSectionQuery
} from '../../../../../features/customReport/queries/customReportQueryKeys'
import {
  createCustomReportSection,
  updateCustomReportSection,
  deleteCustomReportVariable
} from '../../../../../features/customReport/service/customReportSectionService'
import { useDeleteSection } from './useDeleteSection'

interface SectionModalProps {
  visible: boolean
  setVisible: (value: boolean) => void
  reportId?: CustomReport['id']
  section: Partial<CustomReportSectionsType> | null
}

const SectionModal: React.FC<SectionModalProps> = React.memo(({ reportId, section, visible, setVisible }) => {
  const [form] = useForm()
  const { t } = useTranslation()
  const companyId = useSelector(contextCompanyIdSelector)
  const userCompanies = useSelector(currentUserSortedCompaniesSelector)
  const [sectionCopy, setSectionCopy] = useState<Partial<CustomReportSectionsType> | null>(null)
  const [step, setStep] = useState(section ? 1 : 0)
  const date = useSelector(customReportDateSelector)
  const [variableDelete, setVariableDelete] = useState<CustomReportVariable[]>([])
  const [loading, setLoading] = useState(false)
  const sectionType = Form.useWatch('type', form)
  const deleteConfirmModal = useDeleteConfirmModal()
  const queryClient = useQueryClient()
  const deleteSection = useDeleteSection(companyId, reportId)

  const transforToForm = (sec: Partial<CustomReportSectionsType>) => ({
    ...sec,
    ...(!(section as CustomReportTableSection | CustomReportChartSection | CustomReportPieSection)?.params && {
      params: { reallocate: true }
    }),
    variables: (sec as CustomReportTableSection | CustomReportChartSection)?.variables?.map((v, i) => ({
      ...v,
      key: `${i}`,
      params: {
        ...v.params,
        dataType: [v.params?.dataType, v.params?.budgetingScenario?.id].filter(Boolean)
      }
    }))
  })

  useEffect(() => {
    if (section && visible) {
      section ? setStep(1) : setStep(0)
      form.setFieldsValue(transforToForm(section))

      if (section.type === 'infobox') {
        const parsed = parseInfoboxSection(section as CustomReportInfoboxSection)
        // Fill in missing company names from user's company list
        parsed.companyScope = parsed.companyScope.map(cs => ({
          ...cs,
          name: cs.name || userCompanies?.find(c => c.id === cs.id)?.name || cs.id
        }))
        form.setFieldValue('infobox', parsed)
      }
    }
  }, [section])

  const handleCancel = () => {
    setVisible(false)
    form.resetFields()
    setStep(0)
    setSectionCopy(null)
    setVariableDelete([])
  }

  const handleSubmit = async (newSection: Partial<CustomReportSectionsType>) => {
    if (!companyId || !reportId) return
    try {
      setLoading(true)
      // Handle variable deletes before everything yes
      await Promise.all(
        variableDelete.map(variable => deleteCustomReportVariable(companyId, reportId, section!.id!, variable.id!))
      )
      if (section?.id) {
        await updateCustomReportSection(companyId, reportId, section.id, newSection as CustomReportSectionsType, date)
        // Invalidate only the edited section's queries
        queryClient.invalidateQueries({
          queryKey: customReportQueryKeys.sections(),
          predicate: query => isSectionQuery(query, section.id!)
        })
        queryClient.invalidateQueries({ queryKey: customReportQueryKeys.detail(companyId, reportId) })
      } else {
        await createCustomReportSection(companyId, reportId, newSection as CustomReportSectionsType, date)
        queryClient.invalidateQueries({ queryKey: customReportQueryKeys.detail(companyId, reportId) })
      }
      showSuccessMessage()
      handleCancel()
    } catch (error) {
      showErrorMessage()
    } finally {
      setLoading(false)
    }
  }

  const handleStepChange = () => {
    setStep(1)
  }

  const handleSectionCopy = (values: Partial<CustomReportSectionsType>) => {
    setSectionCopy(values)
    form.setFieldsValue(transforToForm(values))

    if (values.type === 'infobox' && values.categories) {
      const parsed = parseInfoboxSection(values as CustomReportInfoboxSection)
      parsed.companyScope = parsed.companyScope.map(cs => ({
        ...cs,
        name: cs.name || userCompanies?.find(c => c.id === cs.id)?.name || cs.id
      }))
      form.setFieldValue('infobox', parsed)
    }
  }

  const handleDelete = () => {
    if (!section?.id) return
    deleteConfirmModal({
      centered: true,
      content: getTitleByLanguage(section as CustomReportSectionsType),
      onOk: async () => {
        await deleteSection(section as CustomReportSectionsType)
        handleCancel()
      }
    })
  }

  const handleValuesChange = ({ type }: Partial<CustomReportSectionsType>) => {
    if (type && type !== 'table') {
      // Filter all title and empty rows
      const vars: CustomReportVariable[] = form.getFieldValue(['variables'])
      const filteredVariables = vars?.filter(v => {
        if (v.rowType === RowType.empty || v.rowType === RowType.title) return false
        return true
      })

      form.setFieldValue('variables', filteredVariables)
    }
  }

  return (
    <Modal
      data-cy="section-modal"
      forceRender={false}
      // eslint-disable-next-line no-nested-ternary
      width={step === 0 ? 400 : sectionType === 'infobox' ? 800 : 1600}
      style={{ top: 20 }}
      styles={
        sectionType === 'infobox'
          ? {
              body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', overflowX: 'hidden' },
              wrapper: { overflow: 'hidden' }
            }
          : undefined
      }
      maskClosable={false}
      title={section ? t('customReportPage:edit-section') : t('customReportPage:add-section')}
      open={visible}
      onCancel={handleCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {step === 1 && section?.id && sectionType === 'infobox' && (
              <Button data-cy="section-delete" danger icon={<DeleteOutlined />} onClick={handleDelete}>
                {t('customReportPage:deleteSection')}
              </Button>
            )}
          </div>
          <Space>
            {step === 0 ? (
              <>
                <Button onClick={handleCancel}>{t('global:cancel')}</Button>
                <Button data-cy="section-continue" onClick={handleStepChange} type="primary">
                  {t('contractsPage:continue')}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleCancel}>{t('global:cancel')}</Button>
                <Button data-cy="section-save" onClick={() => form.submit()} type="primary" loading={loading}>
                  {t('global:save')}
                </Button>
              </>
            )}
          </Space>
        </div>
      }
    >
      {step === 0 ? (
        <CustomReportSectionCopyForm
          handleReset={() => {
            setSectionCopy(null)
            form.resetFields()
          }}
          handleCopy={handleSectionCopy}
        />
      ) : (
        <CustomReportSectionForm
          form={form}
          section={section ?? sectionCopy}
          handleSubmit={handleSubmit}
          handleValuesChange={handleValuesChange}
          setVariableDelete={setVariableDelete}
          variableDelete={variableDelete}
        />
      )}
    </Modal>
  )
})

export default SectionModal
